import * as React from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import { ClientPack, InvoiceState, PaymentStatus } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { formatCurrency, formatDateLocalized } from './utils';
import { generateInvoicePdfAsync } from '../../ajax/requests';
import { PaymentLabel } from '../../components/Forms/fields/PaymentsList';
import { useState } from 'react';
import AppButton from '../../components/AppButton';

const DetailSection = styled.div<{ children?: React.ReactNode }>`
    margin-bottom: var(--spacer-M);
    padding: var(--spacer-M);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-S);
    overflow: auto;
    white-space: nowrap;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: var(--appBlue);
    margin-bottom: var(--spacer-S);
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const DetailRow = styled.div<{ children?: React.ReactNode; className?: string }>`
    display: flex;
    justify-content: space-between;
    padding: var(--spacer-XS) 0;
    border-bottom: var(--border-thin) solid var(--appLigthGrey);

    &:last-child {
        border-bottom: none;
    }

    .label {
        font-weight: 600;
        color: var(--text-muted);
    }

    .value {
        color: var(--text-body);
        font-weight: 500;
    }

    &.highlight {
        background: var(--success-bg);
        padding: var(--spacer-S);
        margin: var(--spacer-XS) calc(var(--spacer-M) * -1);
        padding-left: var(--spacer-M);
        padding-right: var(--spacer-M);
        border: none;

        .value {
            color: var(--success-color);
            font-weight: 700;
            font-size: 18px;
        }
    }
`;

const ExtrasTable = styled.div<{ children?: React.ReactNode }>`
    margin-top: var(--spacer-S);
`;

const ExtraRow = styled.div<{ children?: React.ReactNode; className?: string }>`
    display: grid;
    grid-template-columns: 1fr 80px 100px 100px;
    gap: var(--spacer-M);
    padding: var(--spacer-S) 0;
    border-bottom: var(--border-thin) solid var(--appLigthGrey);

    &:last-child {
        border-bottom: none;
    }

    .extra-name {
        font-weight: 500;
        color: var(--text-body);
    }

    .extra-quantity,
    .extra-unit-price,
    .extra-total {
        text-align: right;
        color: var(--text-muted);
    }

    .extra-total {
        font-weight: 700;
        color: var(--appBlue);
    }
`;

const ExtraHeader = styled(ExtraRow)`
    font-weight: 700;
    color: var(--text-muted);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: var(--border-medium) solid var(--appLigthGrey);

    span:nth-child(2),
    span:nth-child(3),
    span:nth-child(4) {
        text-align: right;
    }
`;

const PaymentsTable = styled.div<{ children?: React.ReactNode }>`
    margin-top: var(--spacer-S);
`;

const PaymentRow = styled.div<{ children?: React.ReactNode }>`
    display: grid;
    grid-template-columns: 1fr 150px 100px;
    gap: var(--spacer-M);
    padding: var(--spacer-S) 0;
    border-bottom: var(--border-thin) solid var(--appLigthGrey);

    &:last-child {
        border-bottom: none;
    }

    .payment-date {
        font-weight: 500;
        color: var(--text-body);
    }

    .payment-type {
        color: var(--text-muted);
    }

    .payment-value {
        text-align: right;
        font-weight: 700;
        color: var(--success-color);
    }
`;

const PaymentHeader = styled(PaymentRow)`
    font-weight: 700;
    color: var(--text-muted);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: var(--border-medium) solid var(--appLigthGrey);

    span:nth-child(3) {
        text-align: right;
    }
`;

const NoDataLabel = styled.div`
    padding: var(--spacer-M);
    text-align: center;
    color: var(--text-placeholder);
    font-style: italic;
`;

const InvoicesTable = styled.div<{ children?: React.ReactNode }>`
    margin-top: var(--spacer-S);
`;

const InvoiceRow = styled.div<{ children?: React.ReactNode }>`
    display: grid;
    grid-template-columns: 150px 120px 120px 1fr;
    gap: var(--spacer-M);
    padding: var(--spacer-S) 0;
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    align-items: center;

    &:last-child {
        border-bottom: none;
    }

    .invoice-number {
        font-weight: 500;
        color: var(--text-body);
    }

    .invoice-date {
        color: var(--text-muted);
        font-size: 14px;
    }

    .invoice-total {
        font-weight: 700;
        color: var(--success-color);
    }

    .invoice-button {
        justify-self: end;
    }
`;

const InvoiceHeader = styled(InvoiceRow)`
    font-weight: 700;
    color: var(--text-muted);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: var(--border-medium) solid var(--appLigthGrey);

    span:last-child {
        text-align: right;
    }
`;

interface SubscriptionDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    subscription: ClientPack | null;
}

const SubscriptionDetailModal: React.FC<SubscriptionDetailModalProps> = ({ isOpen, onClose, subscription }) => {
    const t = useTranslations();
    const [fetching, setFetching] = useState<boolean>(false)

    if (!subscription) return null;

    const handleViewInvoice = async (invoiceId: string) => {
        try {
            if (fetching) return;
            setFetching(true);
            const result = await generateInvoicePdfAsync(invoiceId, true);
            window.open(result.url, '_blank');
        } catch (error) {
            console.error('Failed to generate invoice PDF:', error);
        } finally {
            setFetching(false);
        }
    };

    const payments = (subscription.payments ?? []).filter(p => p.status === PaymentStatus.Manual || p.status === PaymentStatus.Success)

    const basePrice = subscription.price;
    const extrasTotal = subscription.extras?.reduce((sum, extra) => sum + (extra.value * extra.quantity), 0) || 0;
    const totalPriceWithoutVAT = basePrice + extrasTotal;
    const totalPriceWithVAT = totalPriceWithoutVAT * (1 + subscription.vat);

    const issuedInvoices = (subscription.invoices ?? [])
        .filter(i => i.state === InvoiceState.Issued)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                {subscription.packName}
            </ModalHeader>
            <ModalBody>
                {/* General Information */}
                <DetailSection>
                    <SectionTitle>{t(TK.generalInformation)}</SectionTitle>
                    <DetailRow>
                        <span className="label">{t(TK.subscription)}:</span>
                        <span className="value">{subscription.packName}</span>
                    </DetailRow>
                    <DetailRow>
                        <span className="label">{t(TK.validFrom)}:</span>
                        <span className="value">{formatDateLocalized(subscription.validityFrom, t(TK.notAvailable))}</span>
                    </DetailRow>
                    <DetailRow>
                        <span className="label">{t(TK.validTo)}:</span>
                        <span className="value">{formatDateLocalized(subscription.validityTo, t(TK.notAvailable))}</span>
                    </DetailRow>
                    <DetailRow>
                        <span className="label">{t(TK.creationDate)}:</span>
                        <span className="value">{formatDateLocalized(subscription.creationDate, t(TK.notAvailable))}</span>
                    </DetailRow>
                    {subscription.notes && (
                        <DetailRow>
                            <span className="label">{t(TK.notes)}:</span>
                            <span className="value">{subscription.notes}</span>
                        </DetailRow>
                    )}
                </DetailSection>

                {/* Pricing Details */}
                <DetailSection>
                    <SectionTitle>{t(TK.pricingDetails)}</SectionTitle>
                    <DetailRow>
                        <span className="label">{t(TK.basePrice)}:</span>
                        <span className="value">{formatCurrency(basePrice)}</span>
                    </DetailRow>

                    {/* Extras */}
                    {subscription.extras && subscription.extras.length > 0 && (
                        <ExtrasTable>
                            <ExtraHeader>
                                <span>{t(TK.extra)}</span>
                                <span>{t(TK.quantity)}</span>
                                <span>{t(TK.unitPrice)}</span>
                                <span>{t(TK.total)}</span>
                            </ExtraHeader>
                            {subscription.extras.map((extra, index) => (
                                <ExtraRow key={index}>
                                    <span className="extra-name">{extra.name}</span>
                                    <span className="extra-quantity">{extra.quantity}</span>
                                    <span className="extra-unit-price">{formatCurrency(extra.value)}</span>
                                    <span className="extra-total">{formatCurrency(extra.value * extra.quantity)}</span>
                                </ExtraRow>
                            ))}
                        </ExtrasTable>
                    )}

                    <DetailRow>
                        <span className="label">{t(TK.totalPrice)} ({t(TK.withoutVAT)}):</span>
                        <span className="value">{formatCurrency(totalPriceWithoutVAT)}</span>
                    </DetailRow>

                    <DetailRow className="highlight">
                        <span className="label">{t(TK.totalPrice)} ({t(TK.withVAT)}):</span>
                        <span className="value">{formatCurrency(totalPriceWithVAT)}</span>
                    </DetailRow>
                </DetailSection>

                {/* Payments */}
                <DetailSection>
                    <SectionTitle>{t(TK.payments)}</SectionTitle>
                    {payments.length > 0 ? (
                        <PaymentsTable>
                            <PaymentHeader>
                                <span>{t(TK.date)}</span>
                                <span>{t(TK.type)}</span>
                                <span>{t(TK.value)}</span>
                            </PaymentHeader>
                            {payments.map((payment, index) => (
                                <PaymentRow key={index}>
                                    <span className="payment-date">{formatDateLocalized(payment.date, '-')}</span>
                                    <span className="payment-type"><PaymentLabel payment={payment}/></span>
                                    <span className="payment-value">{formatCurrency(payment.value)}</span>
                                </PaymentRow>
                            ))}
                        </PaymentsTable>
                    ) : (
                        <NoDataLabel>{t(TK.noPaymentsFound)}</NoDataLabel>
                    )}
                </DetailSection>

                {/* Invoices */}
                {issuedInvoices.length > 0 && (
                    <DetailSection>
                        <SectionTitle>{t(TK.invoices)}</SectionTitle>
                        <InvoicesTable>
                            <InvoiceHeader>
                                <span>{t(TK.invoiceNumber)}</span>
                                <span>{t(TK.date)}</span>
                                <span>{t(TK.total)}</span>
                                <span></span>
                            </InvoiceHeader>
                            {issuedInvoices.map((invoice) => (
                                <InvoiceRow key={invoice.id}>
                                    <span className="invoice-number">{invoice.number || t(TK.notAvailable)}</span>
                                    <span className="invoice-date">{formatDateLocalized(invoice.date, '-')}</span>
                                    <span className="invoice-total">{formatCurrency(invoice.total)}</span>
                                    <div className="invoice-button">
                                        <AppButton
                                            color="primary"
                                            size="sm"
                                            disabled={fetching}
                                            onClick={() => handleViewInvoice(invoice.id)}
                                        >
                                            {t(TK.viewInvoice)} - {formatCurrency(invoice.total)}
                                        </AppButton>
                                    </div>
                                </InvoiceRow>
                            ))}
                        </InvoicesTable>
                    </DetailSection>
                )}
            </ModalBody>
        </Modal>
    );
};

export default SubscriptionDetailModal;
